/**
 * LearningGatesPage — /omega/learning/gates
 *
 * A5d Sprint 35: Shows V-026 gate status (G-1..G-6) and the per-vertical
 * atom-count heat-map that feeds G-1.
 *
 * Polls GET /api/v1/admin/learning/gates every 60 s.
 * Founder-only (isOmegaAdmin guard).
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

// ── Types ─────────────────────────────────────────────────────────────────────

interface V026GateState {
  g1_atoms_per_layer: boolean;
  g2_three_verticals: boolean;
  g3_papers_ingested: boolean;
  g4_cycles_completed: boolean;
  g5_completion_atoms: boolean;
  g6_foundational_valid: boolean;
  all_gates_open: boolean;
}

interface LayerAtomCount {
  layer: string;
  count: number;
  above_threshold: boolean;
}

interface VerticalAtomBreakdown {
  vertical: string;
  layers: LayerAtomCount[];
  layers_above_threshold: number;
}

interface GatesResponse {
  v026_gate: V026GateState;
  g1_breakdown: VerticalAtomBreakdown[];
  as_of_unix: number;
  backfill_url: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const GATE_LABELS: Record<keyof Omit<V026GateState, 'all_gates_open'>, string> = {
  g1_atoms_per_layer: 'G-1 — ≥300 atoms/layer per active domain',
  g2_three_verticals: 'G-2 — ≥3 vertical teams live',
  g3_papers_ingested: 'G-3 — ≥500 papers ingested total',
  g4_cycles_completed: 'G-4 — ≥7 ingest cycles completed',
  g5_completion_atoms: 'G-5 — CompletionAtom emitted per vertical',
  g6_foundational_valid: 'G-6 — Phase 1 foundational coverage valid',
};

function GateBadge({ pass }: { pass: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 4,
        fontWeight: 600,
        fontSize: 13,
        background: pass ? '#16a34a' : '#dc2626',
        color: '#fff',
        minWidth: 52,
        textAlign: 'center',
      }}
    >
      {pass ? 'PASS' : 'FAIL'}
    </span>
  );
}

function HeatCell({ count, aboveThreshold }: { count: number; aboveThreshold: boolean }) {
  const bg = aboveThreshold ? '#bbf7d0' : count > 0 ? '#fef08a' : '#fee2e2';
  return (
    <td
      style={{
        background: bg,
        textAlign: 'center',
        padding: '4px 8px',
        fontSize: 12,
        fontVariantNumeric: 'tabular-nums',
        border: '1px solid #e5e7eb',
      }}
    >
      {count}
    </td>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function LearningGatesPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['learning-gates'],
    queryFn: () => apiFetch<GatesResponse>('/admin/learning/gates'),
    refetchInterval: 60_000,
  });

  const gate = data?.v026_gate;
  const breakdown = data?.g1_breakdown ?? [];

  // Collect unique layer names across all verticals for column headers.
  const allLayers = Array.from(
    new Set(breakdown.flatMap((v) => v.layers.map((l) => l.layer))),
  ).sort();

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h1 style={{ marginBottom: 4 }}>V-026 Knowledge Gates</h1>
      <p style={{ color: '#6b7280', marginBottom: 20 }}>
        All six conditions must pass simultaneously before the autonomous build loop
        (Phase 41) opens.
        {dataUpdatedAt > 0 && (
          <> Last updated: {new Date(dataUpdatedAt).toLocaleTimeString()}.</>
        )}
      </p>

      {isLoading && <p>Loading gate status…</p>}
      {error && <p style={{ color: '#dc2626' }}>Failed to load gate status.</p>}

      {gate && (
        <>
          {/* ── Gate table ─────────────────────────────────────────────── */}
          <table style={{ borderCollapse: 'collapse', marginBottom: 32, width: '100%' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #e5e7eb' }}>
                  Gate
                </th>
                <th style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {(Object.keys(GATE_LABELS) as Array<keyof typeof GATE_LABELS>).map((key) => (
                <tr key={key}>
                  <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>
                    {GATE_LABELS[key]}
                  </td>
                  <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                    <GateBadge pass={gate[key]} />
                  </td>
                </tr>
              ))}
              <tr style={{ background: gate.all_gates_open ? '#dcfce7' : '#fef2f2', fontWeight: 700 }}>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>
                  All gates open (V-026 ready)
                </td>
                <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                  <GateBadge pass={gate.all_gates_open} />
                </td>
              </tr>
            </tbody>
          </table>

          {/* ── G-1 heat-map ───────────────────────────────────────────── */}
          <h2 style={{ marginBottom: 8 }}>G-1 Atom Counts (per vertical × layer)</h2>
          <p style={{ color: '#6b7280', marginBottom: 12, fontSize: 13 }}>
            Green = ≥300 atoms (threshold met). Yellow = 1–299. Red = 0.
            Each vertical needs ≥9 layers at green to pass G-1.
          </p>
          {breakdown.length === 0 ? (
            <p style={{ color: '#6b7280' }}>
              No tagged atom data yet. Atom tags are written after the first
              ingest cycle completes with atom_tags.db wired.{' '}
              <Link to={data?.backfill_url ?? '/omega/system/backfill'}>
                Run backfill →
              </Link>
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '6px 12px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      Vertical
                    </th>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb' }}>
                      Layers ≥300
                    </th>
                    {allLayers.map((l) => (
                      <th
                        key={l}
                        style={{
                          padding: '6px 8px',
                          border: '1px solid #e5e7eb',
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          fontSize: 11,
                        }}
                      >
                        {l}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((v) => {
                    const layerMap = Object.fromEntries(
                      v.layers.map((l) => [l.layer, l]),
                    );
                    return (
                      <tr key={v.vertical}>
                        <td style={{ padding: '6px 12px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {v.vertical}
                        </td>
                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          {v.layers_above_threshold} / {allLayers.length}
                        </td>
                        {allLayers.map((l) => {
                          const entry = layerMap[l];
                          return entry ? (
                            <HeatCell
                              key={l}
                              count={entry.count}
                              aboveThreshold={entry.above_threshold}
                            />
                          ) : (
                            <td
                              key={l}
                              style={{
                                background: '#f3f4f6',
                                textAlign: 'center',
                                padding: '4px 8px',
                                fontSize: 12,
                                border: '1px solid #e5e7eb',
                                color: '#9ca3af',
                              }}
                            >
                              —
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <Link to="/omega/market-intel">← Market Intel</Link>
            {' · '}
            <Link to={data?.backfill_url ?? '/omega/system/backfill'}>
              System Backfill →
            </Link>
            {' · '}
            <Link to="/omega/research/teams">Research Teams →</Link>
          </div>
        </>
      )}
    </div>
  );
}
